import { Icon, CustomTable, ConfirmationPopup, SuccessModal, Pagination, Search, Loader } from '@ohif/ui';
import React, { useState, useEffect } from 'react';
import EditTeleradiologyPrices from './editTeleradiologyPrices';
import { useNavigate } from 'react-router-dom';
import { customStyle } from '@custom/Utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { useTheme } from "../../ui/src/contextProviders";
import { _routes } from '@custom/Utils/constants/routes';
import getTeleModPrices from '@custom/rtk-apis/Setting/getTeleModPrices';
import deleteTeleModPrice from '@custom/rtk-apis/Setting/deleteTeleModPrice';
import { clearGetTeleModPricesState } from '@custom/store/slices/Setting/getTeleModPriceSlice';
import { _roleType } from '@custom/Utils/constants/appConstants';

interface TeleRadiologyPricesProps {
    creditData: any[];
    setcreditData: React.Dispatch<React.SetStateAction<any[]>>;
    isHospitalData: boolean;
    isTelepathologyEnabled?: boolean;
    isTeleradiologyEnabled?: boolean;
    onTableDataUpdate?: (tableData: any[]) => void;
    hospitalId ?: string
    edit?: boolean;
}

const TeleRadiologyPrices = ({ creditData, setcreditData, isHospitalData, isTelepathologyEnabled, isTeleradiologyEnabled, onTableDataUpdate , hospitalId, edit }: TeleRadiologyPricesProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const role = localStorage.getItem('role') || sessionStorage.getItem('role');
    const navigate = useNavigate();
    const getTeleModPriceSliceResponse = useSelector((state: RootState) => state.getTeleModPriceSliceResponse);

    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [tableData, setTableData] = useState([]);
    const [search, setSearch] = useState('');
    const [pgNumber, setPgNumber] = useState(1);
    const [pgLimit, setPgLimit] = useState(10000);
    const [totalPg, setTotalPg] = useState(100);
    const isDarkMode = useTheme();
    const [searchInputValue, setSearchInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [editData, setEditData] = useState(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);

    const [columns, setColumns] = useState([
        { key: 'modality', title: 'Modality' },
        { key: 'studyRegion', title: 'Study Region', sort: true },
        { key: 'credit', title: 'Credit' },
        { key: 'value', title: 'Credit Value' },
    ]);

    useEffect(() => {
    const role = sessionStorage.getItem('role') || localStorage.getItem('role');
    if (role === _roleType.superAdmin) {
        setIsSuperAdmin(true);
    }
    setColumns([
        { key: 'modality', title: 'Modality' },
        { key: 'studyRegion', title: 'Study Region', sort: true },
        { key: 'credit', title: 'Credit' },
        { key: 'value', title: 'Credit Value' },
        ...(edit && isSuperAdmin ? [{ key: 'actions', title: 'Action' }] : []),
    ]);
    }, [edit, isSuperAdmin]);

    const handlePriceUpdate = (updatedItem) => {
        // Update tableData
        const newTableData = tableData.map((item) =>
          item._id === updatedItem._id ? { ...item, ...updatedItem } : item
        );
        setTableData(
            newTableData.map((item) => ({
                ...item,
                studyRegion: item.modality === 'Histopathology' ? '-' : item.studyRegion,
                value: item.credit * (getTeleModPriceSliceResponse?.result?.creditValue || 0)
            })))
        // Propagate updated tableData to parent
        if (onTableDataUpdate) {
          onTableDataUpdate(newTableData);
        }
      };

    const handleEditClick = (item) => {
        const fullItem = tableData.find((i) => i._id === item._id);
        setEditData(fullItem);
        setShowEditForm(true);
      };

      const actions = role === 'Superadmin' ? [
        {
            label: 'Edit',
            iconName: 'edit',
            onClick: handleEditClick,
        },
    ] : [];

    const onPageChangeHandler = (pg) => {
        setPgNumber(pg);
        // run the get api for the modality pricing with the search, limit and page number
        const payload = { search, limit: pgLimit, pageNumber: pg };
        dispatch(getTeleModPrices(payload));
    };

    const handlePageSizeChange = (e) => {
        setPgLimit(e);
        // run the get api for the modality pricing with the search, limit and page number
        const payload = { search, limit: e, pageNumber: pgNumber };
        dispatch(getTeleModPrices(payload));
    };

    const searchClick = (searchText) => {
        const trimmedSearch = searchText.trim();
        setPgNumber(1);
        setIsLoading(true);

        const payload = {
            limit: pgLimit,
            pageNumber: 1,
            ...(trimmedSearch && { search: trimmedSearch })
        };

        dispatch(getTeleModPrices(payload));
    };

    useEffect(() => {
        if (creditData && creditData.length > 0 ) {
            let filteredData = creditData;
            if (!isTelepathologyEnabled) {
              filteredData = creditData.filter(item => item.modality !== 'Histopathology');
            } else if (!isTeleradiologyEnabled) {
              filteredData = creditData.filter(item => item.modality === 'Histopathology');
            }
            setTableData(
                filteredData.map((item) => ({
                    ...item,
                    studyRegion: item.modality === 'Histopathology' ? '-' : item.studyRegion,
                    value: item.credit * (getTeleModPriceSliceResponse?.result?.creditValue || 0)
                })))// Use teleCreditServices from hospital details
            if (onTableDataUpdate) {
                onTableDataUpdate(filteredData); // Propagate initial tableData
              }
        }
      }, [isTelepathologyEnabled, isTeleradiologyEnabled,getTeleModPriceSliceResponse,creditData]);


    useEffect(() => {
           if (getTeleModPriceSliceResponse?.status === 'SUCCESS' && getTeleModPriceSliceResponse?.result && creditData?.length === 0 && !isHospitalData) {
                const commonTeleCreditData = Array.isArray(getTeleModPriceSliceResponse?.result?.data) ? getTeleModPriceSliceResponse?.result?.data : [];
                if(!isTelepathologyEnabled){
                    const filteredData = commonTeleCreditData.filter(item => item.modality !== 'Histopathology');
                    setTableData(
                    filteredData.map((item) => ({
                        ...item,
                        studyRegion: item.modality === 'Histopathology' ? '-' : item.studyRegion,
                        value: item.credit * (getTeleModPriceSliceResponse?.result?.creditValue || 0)
                    })))
                    if (onTableDataUpdate) {
                        onTableDataUpdate(filteredData); // Propagate tableData to parent
                    }
                } else if(!isTeleradiologyEnabled) {
                    const filteredData = commonTeleCreditData.filter(item => item.modality === 'Histopathology');
                    setTableData(
                    filteredData.map((item) => ({
                        ...item,
                        studyRegion: item.modality === 'Histopathology' ? '-' : item.studyRegion,
                        value: item.credit * (getTeleModPriceSliceResponse?.result?.creditValue || 0)
                    })))
                    if (onTableDataUpdate) {
                        onTableDataUpdate(filteredData); // Propagate tableData to parent
                    }
                } else {
                    setTableData(
                        commonTeleCreditData.map((item) => ({
                            ...item,
                            studyRegion: item.modality === 'Histopathology' ? '-' : item.studyRegion,
                            value: item.credit * (getTeleModPriceSliceResponse?.result?.creditValue || 0)
                        })))

                    if (onTableDataUpdate) {
                        onTableDataUpdate(commonTeleCreditData); // Propagate tableData to parent
                    }
                }
               setTotalPg(getTeleModPriceSliceResponse?.result?.pagination?.totalPages);
               setPgNumber(getTeleModPriceSliceResponse?.result?.pagination?.page);
           }
           setIsLoading(false);
       }, [getTeleModPriceSliceResponse?.status, getTeleModPriceSliceResponse?.result]);

    useEffect(() => {
            setIsLoading(true);
            const payload = { search, limit: pgLimit, pageNumber: pgNumber };
            dispatch(getTeleModPrices(payload));
    }, [dispatch, search, pgLimit, pgNumber,search,isTelepathologyEnabled,isTeleradiologyEnabled]);

    return (
        <>
            <div className='page page__fullBody' style={
                isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
            }>
                {showEditForm ? (
          <EditTeleradiologyPrices setShowEditForm={setShowEditForm} priceModalityData={editData} setTableData={setTableData} onUpdate={handlePriceUpdate} hospitalId={hospitalId} />
        ) : (
                <div className='page__body'>
                    <div className='tableWrap'>
                        <div className='tableContainer'>
                            <CustomTable
                                columns={columns}
                                data={tableData}
                                actions={edit && isSuperAdmin ? actions : []}
                                searchQuery={searchQuery}
                                from='modalityPricing'
                                userDetails={''}
                                setClickedItem={''}
                                clickedItem={''}
                            />
                        </div>
                    </div>
                </div>)}
                <Loader isLoaderOpen={isLoading} />
                <SuccessModal
                    isSuccessModalOpen={isSuccessModalOpen}
                    title={`Modality pricing deleted successfully.`}
                    handleCloseModal={() => setIsSuccessModal(false)}
                />
            </div>
        </>
    );
};

export default TeleRadiologyPrices;
